import { NextPage } from "next";
import Head from "next/head";
import { MouseEventHandler, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { ConditionalKeys } from "type-fest";

import { signupUser } from "~/api/auth/signupUser";
import { sendRequest } from "~/api/clients/axios";
import ApplicationNavigationButton from "~/components/application/navigationButtons";
import ApplicationNextStep from "~/components/application/nextStep";
import ApplicationSections from "~/components/application/sections";
import UnauthenticatedPage from "~/components/wrappers/UnauthenticatedPage";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { CreateMemberInput } from "~/types/user/user.types";
import { emailRegex, passwordRegex } from "~/utils/constants/regex";
import { getObjectKeys } from "~/utils/functions/general.functions";

import styles from "./index.module.scss";

const ApplicationPage: NextPage = () => {
  const [clientSecret] = useState<string>("");

  const methods = useForm<CreateMemberInput>();
  const [step, setStep] = useState<number>(0);
  const [reachedStep, setReachedStep] = useState<number>(1);

  const [passwordText, setPasswordText] = useState("");

  const { language } = useLanguage();
  const applyTexts =
    language === "En"
      ? englishTranslations.applyPage
      : frenchTranslations.applyPage;

  const { mutate: signupMutation } = useMutation(signupUser, {
    onSuccess: () => {
      setStep((prev) => prev + 1);
      setReachedStep((prev) => prev + 1);
    },
  });

  const handleSetPasswordText = (text: string) => {
    setPasswordText(text);
  };

  const handleSubmit = (data: CreateMemberInput) => {
    //MUST handle this appropriately
    delete data.confirmPassword;
    delete data.acceptRules;
    delete data.acceptHealthStatement;

    getObjectKeys(data).forEach((key) => {
      const val = data[key];
      if (val === "" || val === "$_DEFAULT_VALUE_$") {
        delete data[key];
      }
    });
    signupMutation(data);
  };

  const checkStepValidity = (...keys: (keyof CreateMemberInput)[]) => {
    for (const key of keys) {
      if (!methods.getValues(key)) {
        methods.setFocus(key);
        return false;
      }
    }
    return true;
  };

  const passwordValidation = (password: string) => {
    if (password == "") return "";

    const error: string[] = [];

    if (password.length < 8)
      error.push(applyTexts.applicationForm.minimumCharacters);
    if (!password.match(/[a-z]/))
      error.push(applyTexts.applicationForm.lowerCase);
    if (!password.match(/[A-Z]/))
      error.push(applyTexts.applicationForm.upperCase);
    if (!password.match(/[0-9]/)) error.push(applyTexts.applicationForm.number);
    if (!password.match(/[^a-zA-Z0-9]/))
      error.push(applyTexts.applicationForm.specialCharacter);

    return error.join(", ");
  };

  const checkRegex = (
    key: ConditionalKeys<CreateMemberInput, string>,
    regex: RegExp,
    handleSetErrorMessage?: () => void
  ) => {
    if (!regex.test(methods.getValues(key))) {
      if (handleSetErrorMessage) handleSetErrorMessage();
      methods.setFocus(key);
      return false;
    }
    return true;
  };

  const checkConfirmPassword = () => {
    if (methods.getValues("password") != methods.getValues("confirmPassword")) {
      methods.setFocus("confirmPassword");
      return false;
    }
    return true;
  };

  const checkDate = (
    key: ConditionalKeys<CreateMemberInput, Date>,
    min: Date | null = null,
    max: Date | null = null
  ) => {
    const date = new Date(methods.getValues(key));
    if (
      !date ||
      `${date}` == "Invalid Date" ||
      // since this is a date the value should not be greater than min or less than max unlike a normal number
      (!!min && date.valueOf() > min.valueOf()) ||
      (!!max && date.valueOf() < max.valueOf())
    ) {
      methods.setFocus(key);
      return false;
    }
    return true;
  };

  const checkEmailAvailability = async () => {
    const available = await sendRequest<boolean>({
      method: "get",
      url: `/auth/check-availability?email=${methods.getValues("email")}`,
    });
    if (!available) {
      methods.setError("email", {
        type: "manual",
        message:
          language === "En"
            ? "This email is already linked to an account"
            : "Cet email est déjà rattaché à un compte",
      });
      methods.setFocus("email");
      return false;
    }
    return true;
  };

  const checkUsernameAvailability = async () => {
    const available = await sendRequest<boolean>({
      method: "get",
      url: `/auth/check-availability?username=${methods.getValues("username")}`,
    });
    if (!available) {
      methods.setError("username", {
        type: "manual",
        message: "Nom d’utilisateur déjà utilisé",
      });
      methods.setFocus("username");
      return false;
    }
    return true;
  };

  const handleBack: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setStep((prev) => prev - 1);
  };

  const handleNext = async () => {
    switch (step) {
      case 0:
        if (
          !checkStepValidity(
            "firstname",
            "lastname",
            "username"
            // "acceptRules",
            // "acceptHealthStatement"
          ) ||
          !(await checkUsernameAvailability())
        )
          return;
        break;
      case 1:
        if (
          !checkRegex("email", emailRegex) ||
          !checkRegex("password", passwordRegex, () => {
            handleSetPasswordText(
              passwordValidation(methods.getValues("password"))
            );
          }) ||
          !checkConfirmPassword() ||
          !(await checkEmailAvailability())
        )
          return;
        break;
      case 2:
        const date18YearsAgo = new Date(Date.now());
        date18YearsAgo.setFullYear(date18YearsAgo.getFullYear() - 18);
        if (
          !checkStepValidity("phoneNumber") ||
          !checkDate("birthDate", date18YearsAgo)
        )
          return;
        break;
      case 3:
        if (!checkStepValidity("industry", "jobTitle", "companyName")) return;
        break;
      case 4:
        if (!methods.getValues("profilePicturePath")) {
          methods.setError("profilePicturePath", {
            message:
              language === "En"
                ? "Please upload a profile picture"
                : "Veuillez télécharger une photo de profil",
          });
          return;
        }
        break;
      case 5:
        if (!checkStepValidity("shortBiography", "joiningSilencio")) return;
        break;

      case 7:
        handleSubmit(methods.getValues());
        return;
    }
    setStep((prev) => prev + 1);
    if (reachedStep == step) {
      setReachedStep((prev) => prev + 1);
    }
  };

  const goToNextStep = () => {
    setStep((prev) => prev + 1);
    setReachedStep((prev) => prev + 1);
  };

  return (
    <UnauthenticatedPage>
      <Head>
        <title>Silencio - {applyTexts.title}</title>
      </Head>
      <FormProvider {...methods}>
        <div className={styles.applicationFormContainer}>
          <form
            onSubmit={() => {
              methods.handleSubmit(handleSubmit);
            }}
            className={styles.applicationForm}
            autoComplete="off"
          >
            {step !== 0 && <ApplicationNextStep currentStep={step} />}
            <ApplicationSections
              clientSecret={clientSecret}
              step={step}
              reachedStep={reachedStep}
              passwordText={passwordText}
              handleSetPasswordText={handleSetPasswordText}
              goToNextStep={goToNextStep}
            />
            {step < 8 && (
              <ApplicationNavigationButton
                step={step}
                handleBack={handleBack}
                handleNext={(e) => {
                  if (step < 8) e.preventDefault();
                  handleNext();
                }}
              />
            )}
          </form>
        </div>
      </FormProvider>
    </UnauthenticatedPage>
  );
};

export default ApplicationPage;
